import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {take} from 'rxjs/operators';
import {BdManualContractDecisionService} from '../../../service/api/bd-manual-contract-decision.service';
import {BdPermissionService} from '../../../service/api/bd-permission.service';
import {BdPermissionsDto} from '../../../model/dto/api/bd-permissions.dto';
import {
    HANDOVER_APPROVAL_ROLE_HINT,
    isHandOverApproveBlocked
} from '../../../util/bd-hand-over-approval.util';
import {extractError} from '../../../util/error-extraction.util';
import {
    UmUserQlService
} from '../../../../../core/shared/ql/service/ql-user.service';
import {
    UmUserDto
} from '../../../../../feature-core/um/model/dto/entity/um-user.dto';

/**
 * Single hand-over row shown in the approval table.
 * The shape is intentionally permissive — Wave 4 will feed the rows from a QL
 * query; Wave 3 accepts whatever the upstream caller provides.
 */
interface BdHandOverRow {
    id: number;
    submittedBy: string;
    submittedByUserId: number | null;
    submittedAt: string | null;
    action: string;
    contractId: number | null;
    contractName: string | null;
    reason: string | null;
    rejectionReason: string;
    processing: boolean;
    processed: boolean;
    /**
     * Final decision outcome after the row was processed (C10 AK-3): exactly one
     * end-status badge ("Genehmigt"/"Abgelehnt") is shown instead of the prior
     * "Freigabe ausstehend" + "Verarbeitet" duplication. Null while still pending.
     *
     * @ticket TM-Wave-B
     */
    decision: 'APPROVED' | 'REJECTED' | null;
    /** Local UI flag — drives the inline reject form visibility. */
    showRejectForm: boolean;
}

/**
 * AI:
 * Status: 'wave-3-implementation'
 * Type: Component
 * SubType: Exit-UI / Approval
 * Reason: Genehmigungs-UI. Listet alle HandOvers im Status PENDING_APPROVAL
 *         und erlaubt Genehmigen / Ablehnen pro Eintrag. Ablehnungen erfordern einen
 *         Freitext-Grund.
 *
 *         TM-1000002054: 'Genehmigen' needs the role admin, bd_admin or bd_approver
 *         ({@link BdPermissionService}, {@code GET /mvsa/bd/permissions}); without it the
 *         button is disabled and the reason is visible. A user with the role may approve
 *         a hand-over he submitted himself. 'Ablehnen' has no self-lock any more (USER Q3);
 *         reject stays as the backend allows it. The backend checks the role on its own.
 *
 *         Mapped to {@code BdDunningExitUiComponent.HANDOVER_APPROVAL}.
 *
 *         TM-1000001808: User-Namen werden client-seitig via
 *         {@link UmUserQlService#getUserById} aufgelöst, da der QL-semantische
 *         Validator den FK {@code submittedBy} auf {@code bd.DunningHandOver}
 *         (noch) nicht als joinable exposed. Das eingebaute Batching+Caching
 *         (auditTime 120ms) im QL-Service vermeidet N+1 selbst bei vielen Zeilen.
 *
 * @ticket TM-1000001786
 * @ticket TM-1000001808
 * @ticket TM-1000002054
 */
@Component({
    selector: 'bd-handover-approval',
    templateUrl: './bd-handover-approval.component.html',
    styleUrls: ['./bd-handover-approval.component.scss'],
    standalone: false
})
export class BdHandoverApprovalComponent implements OnInit, OnChanges {

    // ----------------------------------------------------------------- //
    // Inputs
    // ----------------------------------------------------------------- //

    @Input() dunning: Record<string, any> | null = null;
    @Input() handOvers: any[] | null = null;
    @Input() uiComponentConfig: Record<string, any> | null = null;
    /**
     * Current user id. Resolves the approver name and keeps 'Genehmigen' locked while it is
     * unresolved (AB-11, TM-1000002054).
     */
    @Input() currentUserId: number | null = null;

    /**
     * Resolved display name of the current approver (C10 AK-7). Shown as
     * "Freigabe durch <Name>". Resolved client-side from {@link currentUserId}.
     *
     * @ticket TM-Wave-B
     */
    currentUserName: string | null = null;

    // ----------------------------------------------------------------- //
    // Outputs
    // ----------------------------------------------------------------- //

    @Output() actionSubmitted = new EventEmitter<{handOverId: number; decision: 'APPROVED' | 'REJECTED'}>();

    // ----------------------------------------------------------------- //
    // State
    // ----------------------------------------------------------------- //

    initialized: boolean = false;
    errorMessage: string | null = null;

    rows: BdHandOverRow[] = [];

    /**
     * TM-1000002054: approval right of the signed-in user (role admin, bd_admin or
     * bd_approver). {@code null} while the read is pending — 'Genehmigen' stays disabled
     * until the server confirms the right.
     */
    mayApprove: boolean | null = null;

    /** TM-1000002054: hint shown when the approval right is missing (one source: the util). */
    readonly approvalRoleHint: string = HANDOVER_APPROVAL_ROLE_HINT;

    constructor(
        private readonly manualContractDecisionService: BdManualContractDecisionService,
        private readonly userQlService: UmUserQlService,
        private readonly bdPermissionService: BdPermissionService // TM-1000002054
    ) {}

    // ----------------------------------------------------------------- //
    // Lifecycle
    // ----------------------------------------------------------------- //

    /**
     * TM-1000001808: Nach dem Aufbau der Zeilen werden die User-Namen für alle
     * Einträge mit gesetzter {@code submittedByUserId} client-seitig nachgeladen.
     */
    ngOnInit(): void {
        this.rows = this.buildRows(this.handOvers);
        this.enrichSubmittedByNames(this.rows);
        this.resolveCurrentUserName();
        this.loadApprovalPermission(); // TM-1000002054
        this.initialized = true;
    }

    /**
     * TM-1000002054: reads the approval right once (the service caches it per session and
     * answers {@code false} on a failed read). The shared read completes after one value.
     */
    private loadApprovalPermission(): void {
        this.bdPermissionService.load()
            .pipe(take(1))
            .subscribe((permissions: BdPermissionsDto) => {
                this.mayApprove = permissions?.handOverApprove === true;
            });
    }

    /**
     * Re-initialises rows when the handOvers input is updated after initial
     * mount. ngComponentOutlet may set inputs after ngOnInit fires, so we must
     * react to changes here to pick up the handOvers array.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['handOvers'] && !changes['handOvers'].firstChange) {
            this.rows = this.buildRows(this.handOvers);
            this.enrichSubmittedByNames(this.rows);
        }
        if (changes['currentUserId'] && !changes['currentUserId'].firstChange) {
            this.resolveCurrentUserName();
        }
    }

    /**
     * Resolves the current approver's display name from {@link currentUserId}
     * for the "Freigabe durch <Name>" line (C10 AK-7). Uses the batching/caching
     * {@link UmUserQlService#getUserById}; falls back to null (UI shows a generic
     * "aktueller Benutzer" hint) when the user cannot be resolved.
     *
     * @ticket TM-Wave-B
     */
    private resolveCurrentUserName(): void {
        if (this.currentUserId == null) {
            this.currentUserName = null;
            return;
        }
        this.userQlService.getUserById(this.currentUserId)
            .pipe(take(1))
            .subscribe((user: UmUserDto | null) => {
                if (!user) {
                    return;
                }
                const combined = `${(user.firstName ?? '').trim()} ${(user.lastName ?? '').trim()}`.trim();
                this.currentUserName = combined.length > 0 ? combined : null;
            });
    }

    // ----------------------------------------------------------------- //
    // Actions
    // ----------------------------------------------------------------- //

    /**
     * TM-1000001974 / TM-1000002054: true while the current user could NOT be resolved to
     * a numeric id. 'Genehmigen' stays disabled then (AB-11, signed S-15).
     */
    get currentUserUnresolved(): boolean {
        return this.currentUserId == null;
    }

    /** TM-1000002054: true while the approval-right read is still pending. */
    get permissionPending(): boolean {
        return this.mayApprove === null;
    }

    /** TM-1000002054: true when the server answered that the user lacks the approval right. */
    get approvalRoleMissing(): boolean {
        return this.mayApprove === false;
    }

    /**
     * TM-1000002054: single source of truth for whether 'Genehmigen' is blocked for a row:
     * no confirmed approval right (also while the read is pending), an unresolved current
     * user, or the row's request in flight. Submitter == approver is NOT a reason any more.
     */
    isApproveBlocked(row: BdHandOverRow): boolean {
        return isHandOverApproveBlocked(this.mayApprove, this.currentUserUnresolved, row.processing);
    }

    approve(row: BdHandOverRow): void {
        // Defensive guards behind the disabled binding; the backend checks the role itself.
        if (this.currentUserUnresolved) {
            this.errorMessage = 'Benutzer konnte nicht ermittelt werden — Freigabe nicht möglich.';
            return;
        }
        if (this.mayApprove !== true) { // TM-1000002054
            this.errorMessage = this.approvalRoleHint;
            return;
        }
        this.errorMessage = null;
        row.processing = true;

        // userId is ignored by the backend (it uses the authenticated principal),
        // so we let the service default the param instead of passing a possibly-null id.
        this.manualContractDecisionService.approveHandOver(row.id).subscribe({
            next: () => {
                row.processing = false;
                row.processed = true;
                row.decision = 'APPROVED';
                this.actionSubmitted.emit({handOverId: row.id, decision: 'APPROVED'});
            },
            error: (err) => {
                row.processing = false;
                this.errorMessage = extractError(err);
            }
        });
    }

    /**
     * Toggles the reject form for a single row. Submitting is handled separately
     * by {@link confirmReject}.
     */
    toggleRejectForm(row: BdHandOverRow): void {
        row.showRejectForm = !row.showRejectForm;
    }

    confirmReject(row: BdHandOverRow): void {
        if (!row.rejectionReason || row.rejectionReason.trim().length === 0) {
            this.errorMessage = 'Bitte einen Ablehnungsgrund angeben.';
            return;
        }
        // The backend derives the rejector from the authenticated principal, so a missing
        // currentUserId must NOT block the click. TM-1000002054 (USER Q3): rejecting an own
        // submission is allowed — the former UI-only self-lock is gone.
        this.errorMessage = null;
        row.processing = true;

        // userId is ignored by the backend (it uses the authenticated principal),
        // so we let the service default the param instead of passing a possibly-null id.
        this.manualContractDecisionService.rejectHandOver(row.id, row.rejectionReason.trim()).subscribe({
            next: () => {
                row.processing = false;
                row.processed = true;
                row.decision = 'REJECTED';
                row.showRejectForm = false;
                this.actionSubmitted.emit({handOverId: row.id, decision: 'REJECTED'});
            },
            error: (err) => {
                row.processing = false;
                this.errorMessage = extractError(err);
            }
        });
    }

    // ----------------------------------------------------------------- //
    // Template helpers
    // ----------------------------------------------------------------- //

    /**
     * Übersetzt rohe Aktions-Codes (z.B. {@code HANDOVER}, {@code STORNO},
     * {@code WRITE_OFF}) in fachliche deutsche Klartext-Labels (C04). Unbekannte
     * Codes werden unverändert, aber lesbar (Unterstriche → Leerzeichen)
     * zurückgegeben statt als roher technischer Code.
     */
    getActionLabel(action: string | null | undefined): string {
        if (!action || action === '—') {
            return '—';
        }
        const labels: Record<string, string> = {
            HANDOVER:   'Inkasso-Übergabe (INKA)',
            HAND_OVER:  'Inkasso-Übergabe (INKA)',
            STORNO:     'Storno',
            CANCEL:     'Storno',
            WRITE_OFF:  'Abschreibung',
            WRITEOFF:   'Abschreibung',
            PAUSE:      'Mahnpause',
            RATE_AGREEMENT: 'Ratenvereinbarung',
            SPECIAL_HANDLING: 'Sonderbehandlung'
        };
        const key = action.toUpperCase();
        return labels[key] ?? action.replace(/_/g, ' ');
    }

    /**
     * Zeigt einen lesbaren Einreicher-Namen an (C04). Falls die client-seitige
     * Namensauflösung ({@link enrichSubmittedByNames}) fehlschlägt und nur eine
     * technische Benutzer-URN (z.B. {@code um:1:e2e-test-user}) vorliegt, wird
     * daraus ein lesbarer Klarname extrahiert statt die rohe URN anzuzeigen.
     */
    getSubmitterLabel(submittedBy: string | null | undefined): string {
        if (!submittedBy || submittedBy === '—') {
            return '—';
        }
        // URN-Form "um:<tenant>:<benutzername>" → letzten Abschnitt als Klarname.
        if (submittedBy.startsWith('um:')) {
            const parts = submittedBy.split(':');
            const raw = parts[parts.length - 1] ?? submittedBy;
            const readable = raw.replace(/[-_]/g, ' ').trim();
            return readable.length > 0
                ? readable.replace(/\b\w/g, c => c.toUpperCase())
                : submittedBy;
        }
        return submittedBy;
    }

    // ----------------------------------------------------------------- //
    // Internal helpers
    // ----------------------------------------------------------------- //

    private buildRows(handOvers: any[] | null): BdHandOverRow[] {
        if (!handOvers) {
            return [];
        }
        return handOvers.map(h => ({
            id: h?.id ?? h?.handOverId ?? 0,
            submittedBy: h?.submittedBy ?? h?.submittedByName ?? '—',
            submittedByUserId: h?.submittedByUserId ?? h?.createdBy ?? null,
            submittedAt: h?.submittedAt ?? h?.createdAt ?? null,
            // C10 AK-2: these rows are always Inkasso-Übergaben (bd.DunningHandOver).
            // The DTO carries no per-row action field, so default to the real
            // business action instead of leaving "Aktion: —".
            action: h?.action ?? h?.handOverAction ?? 'HANDOVER',
            contractId: h?.contractId ?? null,
            contractName: h?.contractName ?? h?.contractIdentifier ?? null,
            reason: h?.reason ?? h?.comment ?? null,
            rejectionReason: '',
            processing: false,
            processed: false,
            decision: null,
            showRejectForm: false
        }));
    }

    /**
     * TM-1000001808: Reichert die Anzeige-Spalte {@code submittedBy} je Zeile
     * mit dem aufgelösten Namen ({@code firstName lastName}) an. Nutzt
     * {@link UmUserQlService#getUserById}, das eingebautes Batching+Caching
     * besitzt (auditTime 120ms), wodurch alle Calls aus dieser Liste in eine
     * einzige QL-Anfrage konsolidiert werden. Bei nicht auffindbaren Usern
     * bleibt das Fallback {@code '—'} bestehen.
     */
    private enrichSubmittedByNames(rows: BdHandOverRow[]): void {
        for (const row of rows) {
            if (row.submittedByUserId == null) {
                continue;
            }
            this.userQlService.getUserById(row.submittedByUserId)
                .pipe(take(1))
                .subscribe((user: UmUserDto | null) => {
                    if (!user) {
                        return;
                    }
                    const first = (user.firstName ?? '').trim();
                    const last = (user.lastName ?? '').trim();
                    const combined = `${first} ${last}`.trim();
                    if (combined.length > 0) {
                        row.submittedBy = combined;
                    }
                });
        }
    }
}
